import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Link } from 'wouter';

import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';

import QuizApi from '../QuizApi';
import AdminHeader from './AdminHeader';
import adminStatusContext from '../context/adminStatusContext';
import { absURL } from '../utils';

export default function AdminGroupOverview() {

  const adminCtx = useContext(adminStatusContext);
  const isOrgAdmin = adminCtx && adminCtx.isOrgAdmin;

  const [quizGroups, setQuizGroups] = useState([]);
  const [ungroupedCount, setUngroupedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [courseSession, setCourseSession] = useState(null);
  const [startingCourseSession, setStartingCourseSession] = useState(null);
  const [activeSessionByGroup, setActiveSessionByGroup] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const [result, activeResult] = await Promise.all([
      QuizApi.userQuizListGet(),
      QuizApi.courseSessionActiveList()
    ]);
    const groups = result?.response?.quizGroups ?? [];
    setQuizGroups(groups);
    const ungrouped = groups.find(g => g.id === null);
    setUngroupedCount(ungrouped ? ungrouped.quizzes.length : 0);

    const sessions = activeResult?.response?.sessions ?? [];
    setActiveSessionByGroup(Object.fromEntries(sessions.map(s => [s.quizGroupId, s])));

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createGroup = useCallback(async () => {
    if (!newGroupName.trim()) return;
    setSavingGroup(true);
    await QuizApi.quizGroupCreate(newGroupName.trim());
    setNewGroupName('');
    setShowNewGroup(false);
    setSavingGroup(false);
    load();
  }, [newGroupName, load]);

  const startCourseSession = useCallback(async (group, evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    // A group can only have one active session at a time (starting a new one
    // ends the previous one server-side) — if one is already running, just
    // show its existing join info instead of creating a duplicate, which
    // would strand already-connected participants in the now-ended room.
    const existing = activeSessionByGroup[group.id];
    if (existing) {
      setCourseSession({ groupName: group.name, ...existing });
      return;
    }

    setStartingCourseSession(group.id);
    const result = await QuizApi.courseSessionCreate(group.id);
    setStartingCourseSession(null);

    if (result?.success && result.response) {
      setCourseSession({ groupName: group.name, courseSessionId: result.response.id, ...result.response });
      setActiveSessionByGroup(prev => ({
        ...prev,
        [group.id]: { quizGroupId: group.id, courseSessionId: result.response.id, ...result.response }
      }));
    }
  }, [activeSessionByGroup]);

  const endCourseSession = useCallback(async () => {
    if (!courseSession) return;

    await QuizApi.courseSessionEnd(courseSession.courseSessionId);
    setCourseSession(null);
    load();
  }, [courseSession, load]);

  const namedGroups = quizGroups.filter(g => g.id !== null);

  return (
    <div id="userMainPage">
      {loading && (
        <Container>
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        </Container>
      )}

      {!loading && (
        <div>
          <AdminHeader />
          <div className="adminContentWrapper">

            {/* ── Top action bar ── */}
            <div className="d-flex align-items-center flex-wrap mt-2 ml-2" style={{ gap: '8px' }}>
              <Link href={absURL('/admin/createQuiz')}>
                <Button className="btn-sm btn-quizPrimaryDark">Skapa quiz</Button>
              </Link>

              <Link href={absURL('/admin/all')}>
                <Button variant="outline-secondary" className="btn-sm">Se alla</Button>
              </Link>

              {isOrgAdmin && (
                <Link href={absURL('/admin/settings')}>
                  <Button variant="outline-secondary" className="btn-sm" title="Organisationsinställningar">
                    <FontAwesomeIcon icon={faCog} className="mr-1" />
                    Inställningar
                  </Button>
                </Link>
              )}

              {!showNewGroup ? (
                <Button
                  variant="outline-secondary"
                  className="btn-sm"
                  onClick={() => setShowNewGroup(true)}>
                  Ny grupp
                </Button>
              ) : (
                <div className="d-flex flex-wrap align-items-center" style={{ gap: '6px' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: '180px' }}
                    placeholder="Gruppnamn..."
                    value={newGroupName}
                    autoFocus
                    onChange={e => setNewGroupName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') createGroup();
                      if (e.key === 'Escape') setShowNewGroup(false);
                    }}
                  />
                  <Button
                    className="btn-sm btn-quizPrimaryDark"
                    disabled={savingGroup || !newGroupName.trim()}
                    onClick={createGroup}>
                    Spara
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="btn-sm"
                    onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>
                    Avbryt
                  </Button>
                </div>
              )}
            </div>

            {/* ── Group cards ── */}
            <div className="mt-4 ml-2" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px',
              maxWidth: '900px'
            }}>

              {namedGroups.map(group => (
                <div key={group.id} style={{
                  padding: '20px 18px',
                  borderRadius: '12px',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.15s, transform 0.15s',
                  borderLeft: '4px solid #7B7C6A',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.14)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                  <Link href={absURL(`/admin/all?group=${group.id}`)}>
                    <div style={{ cursor: 'pointer' }}>
                      <div style={{ fontWeight: 700, fontSize: '16px', color: '#333', marginBottom: '6px' }}>
                        {group.name}
                      </div>
                      <div style={{ color: '#888', fontSize: '13px' }}>
                        {group.quizzes.length} {group.quizzes.length === 1 ? 'quiz' : 'quizzar'}
                      </div>
                    </div>
                  </Link>
                  {activeSessionByGroup[group.id] && (
                    <div style={{ color: '#7B7C6A', fontSize: '12px', fontWeight: 600, marginTop: '10px' }}>
                      Kurstillfälle aktivt
                    </div>
                  )}
                  <Button
                    variant="outline-secondary"
                    className="btn-sm mt-2"
                    disabled={startingCourseSession === group.id}
                    onClick={evt => startCourseSession(group, evt)}>
                    {startingCourseSession === group.id
                      ? 'Startar...'
                      : (activeSessionByGroup[group.id] ? 'Visa QR-kod' : 'Starta kurstillfälle')}
                  </Button>
                </div>
              ))}

              {ungroupedCount > 0 && (
                <Link href={absURL('/admin/all?group=none')}>
                  <div style={{
                    padding: '20px 18px',
                    borderRadius: '12px',
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s, transform 0.15s',
                    borderLeft: '4px solid #ccc',
                  }}
                    onMouseEnter={e => {
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.14)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                    <div style={{ fontWeight: 700, fontSize: '16px', color: '#888', marginBottom: '6px' }}>
                      Utan grupp
                    </div>
                    <div style={{ color: '#aaa', fontSize: '13px' }}>
                      {ungroupedCount} {ungroupedCount === 1 ? 'quiz' : 'quizzar'}
                    </div>
                  </div>
                </Link>
              )}

              {namedGroups.length === 0 && ungroupedCount === 0 && (
                <p className="text-muted">Inga quizzar ännu.</p>
              )}

            </div>
          </div>
        </div>
      )}

      <Modal show={!!courseSession} onHide={() => setCourseSession(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Kurstillfälle: {courseSession?.groupName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p>
            Deltagarna scannar QR-koden och skriver sitt namn <strong>en gång</strong>.
            När du sedan startar ett quiz i gruppen hamnar de där automatiskt.
          </p>
          {courseSession?.joinUrlQrCode && (
            <img style={{ width: '250px' }} src={courseSession.joinUrlQrCode} alt="" />
          )}
          <div className="mt-3">
            <a target="_blank" rel="noreferrer" href={courseSession?.joinUrl}>{courseSession?.joinUrl}</a>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={endCourseSession}>Avsluta kurstillfälle</Button>
          <Button variant="secondary" onClick={() => setCourseSession(null)}>Stäng</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
